import {
  buildPasskeyPlatformHeaders,
  getPasskeyUserApiBaseUrl,
  type PasskeyPlatformRequestContext,
} from '@modules/passkey/helpers/passkey-api-config.helper';
import { passkeyPlatformRequestTimeoutMs } from '@shared/constants/time.constants';
import type {
  PasskeyAuthenticateBeginRequest,
  PasskeyApiService,
  PasskeyForgotPasswordVerifyRequest,
} from '@modules/passkey/interfaces/passkey-api.interface';
import type {
  PasskeyCeremonyData,
  PlatformApiResult,
} from '@modules/passkey/interfaces/passkey-platform-api.interface';

export class PasskeyPlatformApiError extends Error {
  readonly code: number;

  constructor(code: number, message: string) {
    super(message);
    this.name = 'PasskeyPlatformApiError';
    this.code = code;
  }
}

const parsePlatformResult = async <T>(response: Response): Promise<T> => {
  let payload: PlatformApiResult<T> | undefined;

  try {
    payload = (await response.json()) as PlatformApiResult<T>;
  } catch {
    throw new PasskeyPlatformApiError(-1, `Passkey API returned invalid JSON (HTTP ${response.status})`);
  }

  if (!payload || typeof payload.code !== 'number') {
    throw new PasskeyPlatformApiError(-1, `Passkey API returned unexpected payload (HTTP ${response.status})`);
  }

  if (payload.code !== 0) {
    throw new PasskeyPlatformApiError(payload.code, payload.msg ?? 'Passkey API request failed');
  }

  return payload.data;
};

const normalizePostJsonError = (error: unknown): Error => {
  if (error instanceof PasskeyPlatformApiError) {
    return error;
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return new PasskeyPlatformApiError(-1, 'Passkey API request timed out');
  }

  return error instanceof Error ? error : new Error(String(error));
};

const postJson = async <T>(path: string, body: unknown, headers: HeadersInit): Promise<T> => {
  const baseUrl = getPasskeyUserApiBaseUrl();
  if (!baseUrl) {
    throw new Error('Passkey platform API base URL is not configured');
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), passkeyPlatformRequestTimeoutMs);

  try {
    const response = await fetch(`${baseUrl}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    if (!response.ok && response.status >= 500) {
      throw new PasskeyPlatformApiError(-1, `Passkey API server error (HTTP ${response.status})`);
    }

    return await parsePlatformResult<T>(response);
  } catch (error) {
    throw normalizePostJsonError(error);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const createPasskeyPlatformApiService = (context: PasskeyPlatformRequestContext = {}): PasskeyApiService => ({
  beginAuthenticate: (request: PasskeyAuthenticateBeginRequest) =>
    postJson<PasskeyCeremonyData>(
      '/passkey/authenticate/options',
      request.account ? { account: request.account } : {},
      buildPasskeyPlatformHeaders(context),
    ),

  verifyForgotPassword: async (request: PasskeyForgotPasswordVerifyRequest) => {
    await postJson<unknown>(
      `/passkey/authenticate/forgot-password/verify?account=${encodeURIComponent(request.account)}`,
      {
        ceremonyId: request.ceremonyId,
        credentialJson: request.credentialJson,
      },
      buildPasskeyPlatformHeaders(context),
    );
  },
});
